<script lang="ts">
const omitHeaders = ['orderRef'];
</script>

<script setup lang="ts">
import { computed, onUnmounted, shallowRef, watch } from 'vue';
import MarketplaceBatchCancelAllDialog from '@/modules/marketplace/components/MarketplaceBatchCancelAllDialog.vue';
import MarketplaceBatchModifyAllDialog from '@/modules/marketplace/components/MarketplaceBatchModifyAllDialog.vue';
import MarketplaceCancelAllOrdersDialog from '@/modules/marketplace/components/MarketplaceCancelAllOrdersDialog.vue';
import MarketplaceOrdersTable from '@/modules/marketplace/components/MarketplaceOrdersTable.vue';
import MarketplaceOrdersUploadDialog from '@/modules/marketplace/components/MarketplaceOrdersUploadDialog.vue';
import {
  FrontendOrder,
  QueryOrdersFilterSchema,
} from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { FilterModel, IRowNode, LoadSuccessParams, SortModelItem } from 'ag-grid-enterprise';
import { marketplaceService } from '@/connect/services/marketplace';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { create } from '@/connect';
import { useSocketStore } from '@/store/socket-store';
import { store as vuex } from '@/store/store';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

const emit = defineEmits<{
  (event: 'create-order', side: 'BORROW' | 'LOAN'): void;
  (event: 'action', data: { orderRef: string; message: string; error?: boolean }): void;
  (event: 'view-order', orderRef: string): void;
  (event: 'edit-order', order: FrontendOrder): void;
}>();

const snackbarManager = useSnackbarManager();

const securitiesStore = useSecuritiesStore();

const hasTraderUserRole = computed(() => vuex.getters.hasTraderUserRole);
const canBorrow = computed(() => vuex.getters.canBorrow);
const canLend = computed(() => vuex.getters.canLend);

const showAllMarketplaceOrders = shallowRef(false);
const selectedItems = shallowRef<FrontendOrder[]>([]);
const openDialog = shallowRef<'cancelAll' | 'modifyAll' | 'cancelAllOrders' | null>(null);
const cancelAllInProgress = shallowRef(false);
const sort = shallowRef<SortModelItem>({ colId: 'updatedAt', sort: 'desc' });

const uploadDialog = shallowRef<Side | null>(null);

const tableRefresh = shallowRef((_config: { purge: boolean }) => {
  // empty
});
const resetSelection = shallowRef(() => {
  // empty
});
const refreshCellsByCriteria = shallowRef(
  (_criteria: (node: IRowNode<FrontendOrder>) => boolean, _columns: string[]) => {
    // empty
  }
);
const totalCount = shallowRef(0);

const socketStore = useSocketStore();

// when order changed (my company orders)
socketStore.useEventHandler('refreshOmsOrders', refreshOmsTable);

// when top-of-book changed (counterparty orders), my top-of-book columns need to be updated
socketStore.useEventHandler('refreshVenueTopOfBook', refreshOmsTable);

// when price changes, refresh only the priceChange column cells for affected rows
socketStore.useEventHandler('refreshInstrumentPrice', refreshPriceColumn);

// track cancel-all progress to disable button while in progress
socketStore.useEventHandler('cancelAllOrdersProgress', (payload) => {
  if (payload.status === 'in_progress') {
    cancelAllInProgress.value = true;
  } else if (payload.status === 'completed' || payload.status === 'failed') {
    cancelAllInProgress.value = false;
  }
});

function refreshOmsTable(): void {
  // table is expected to remain more or less the same,
  // no need to purge the cache, just refresh and update the rows
  tableRefresh.value({ purge: false });
}

function refreshPriceColumn(payload: { cusip: string }): void {
  refreshCellsByCriteria.value((node) => node.data?.cusip === payload.cusip, ['priceChange']);
}

watch(showAllMarketplaceOrders, () => {
  resetSelection.value();
  // purge the cache to avoid slow UI animations
  tableRefresh.value({ purge: true });
});

function onReady(config: {
  refresh: (config: { purge: boolean }) => void;
  resetSelection: () => void;
  refreshCellsByCriteria: (
    criteria: (node: IRowNode<FrontendOrder>) => boolean,
    columns: string[]
  ) => void;
}): void {
  tableRefresh.value = config.refresh;
  resetSelection.value = config.resetSelection;
  refreshCellsByCriteria.value = config.refreshCellsByCriteria;
}

onUnmounted(async () => {
  await securitiesStore.unsubscribeFromSocketPriceUpdates();
});

async function queryData(config: {
  page: number;
  pageSize: number;
  sort: SortModelItem;
  filter: FilterModel;
  signal: AbortSignal;
}): Promise<LoadSuccessParams | undefined> {
  const result = await marketplaceService.queryOrders({
    ...config,
    filter: create(QueryOrdersFilterSchema, {
      ...config.filter,
      showAll: showAllMarketplaceOrders.value,
      offsetInMinutesFromUtc: new Date().getTimezoneOffset(),
    }),
  });

  if (!result.success) {
    snackbarManager.show({ message: i18n.t(result.error, result.details) as string });
    return;
  }

  securitiesStore.addSecurities(result.data.instruments);
  void securitiesStore.subscribeToSocketPriceUpdates(result.data.instruments);
  totalCount.value = Number(result.data.totalCount);

  return { rowData: result.data.orders, rowCount: Number(result.data.totalCount) };
}

// for tests only
defineExpose({
  selectedItems,
  totalCount,
});
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <!-- @TODO: check if borrow/lend buttons will be restricted to certain users -->
    <div class="pa-sm-2 gap-1 d-flex flex-wrap align-center">
      <AuroraBtnDropdown
        v-if="canBorrow"
        color="secondary"
        data-test="borrow-button"
        :disabled="!hasTraderUserRole"
        main-text="Borrow"
        split
        timeframe="createLoans"
        @click="emit('create-order', 'BORROW')"
      >
        <VList dense>
          <VListItem data-test="upload-demand-orders" @click="uploadDialog = Side.BORROW">
            <VListItemContent>
              <VListItemTitle>Upload Demand Orders</VListItemTitle>
            </VListItemContent>
            <VListItemAction>
              <VIcon dense small> mdi-upload</VIcon>
            </VListItemAction>
          </VListItem>
        </VList>
      </AuroraBtnDropdown>

      <AuroraBtnDropdown
        v-if="canLend"
        color="secondary"
        data-test="lend-button"
        :disabled="!hasTraderUserRole"
        main-text="Lend"
        split
        timeframe="createLoans"
        @click="emit('create-order', 'LOAN')"
      >
        <VList dense>
          <VListItem data-test="upload-supply-orders" @click="uploadDialog = Side.LOAN">
            <VListItemContent>
              <VListItemTitle>Upload Supply Orders</VListItemTitle>
            </VListItemContent>
            <VListItemAction>
              <VIcon dense small> mdi-upload</VIcon>
            </VListItemAction>
          </VListItem>
        </VList>
      </AuroraBtnDropdown>

      <template v-if="selectedItems.length">
        <span class="ml-2 mr-2">
          {{ selectedItems.length ? `${selectedItems.length}/${totalCount} selected` : '' }}
        </span>

        <AuroraBtn
          color="red"
          data-test="batch-action-cancel"
          :disabled="!hasTraderUserRole"
          small
          timeframe="createLoans"
          @click="openDialog = 'cancelAll'"
        >
          Cancel
        </AuroraBtn>

        <AuroraBtn
          color="secondary"
          data-test="order-list-edit-btn"
          :disabled="!hasTraderUserRole"
          small
          timeframe="createLoans"
          @click="openDialog = 'modifyAll'"
        >
          Edit
        </AuroraBtn>
      </template>

      <AuroraBtn
        v-if="!selectedItems.length && hasTraderUserRole && totalCount > 0"
        color="red"
        data-test="cancel-all-orders-btn"
        :disabled="cancelAllInProgress"
        small
        timeframe="createLoans"
        @click="openDialog = 'cancelAllOrders'"
      >
        Cancel All Orders
      </AuroraBtn>

      <VTooltip bottom>
        <template #activator="{ on, attrs }">
          <span v-bind="attrs" class="ml-auto" v-on="on">
            <VSwitch
              v-model="showAllMarketplaceOrders"
              class="mt-0"
              data-test="show-all-orders"
              :false-value="false"
              hide-details
              label="show archived"
            />
          </span>
        </template>
        <span class="tip-background">
          Enabling show archived will include in the results Orders that were closed before today
        </span>
      </VTooltip>
    </div>

    <!-- Dialog will not monitor changes to selectedItems list, it will only be used to initialize a local list on mount. -->
    <MarketplaceBatchCancelAllDialog
      v-if="openDialog === 'cancelAll'"
      :items="selectedItems"
      @back="openDialog = null"
      @close-modal="openDialog = null"
      @success="
        selectedItems = [];
        resetSelection();
      "
    />

    <!-- Dialog will not monitor changes to selectedItems list, it will only be used to initialize a local list on mount. -->
    <MarketplaceBatchModifyAllDialog
      v-if="openDialog === 'modifyAll'"
      :items="selectedItems"
      v-on="$listeners"
      @back="openDialog = null"
      @close-modal="openDialog = null"
      @success="
        selectedItems = [];
        resetSelection();
      "
    />

    <MarketplaceCancelAllOrdersDialog
      v-if="openDialog === 'cancelAllOrders'"
      @back="openDialog = null"
      @close-modal="openDialog = null"
      @success="
        cancelAllInProgress = false;
        tableRefresh({ purge: true });
      "
    />

    <MarketplaceOrdersTable
      data-test="marketplace-orders-table"
      :omit-headers="omitHeaders"
      :query-data="queryData"
      :selected-items.sync="selectedItems"
      :show-all="showAllMarketplaceOrders"
      :sort="sort"
      @action="(data) => emit('action', data)"
      @edit-order="(order) => emit('edit-order', order)"
      @ready="onReady"
      @view-order="(orderRef) => emit('view-order', orderRef)"
    />

    <MarketplaceOrdersUploadDialog
      v-if="uploadDialog !== null"
      :side="uploadDialog"
      @close-modal="uploadDialog = null"
    />
  </VCard>
</template>

<style lang="scss" scoped>
.gap-1 {
  gap: 1rem;
}

.simple-security-search {
  width: 2.5rem;
}

.side {
  width: 2.5rem;
}

.filter-list {
  width: 2.5rem;
}
</style>
